import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled, { CSSProperties } from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { ANY_SPORT_MY_CARDS_BOXES } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { isTypeFn } from '@sorare/core/src/gql';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { FixtureOverview } from 'components/fixture/FixtureTeamsOverview/FixtureOverview';
import {
  RewardOverviewChip,
  rgbColorPerRarity,
} from 'components/rewards/RewardOverview';
import DeliverableItemOrderFormDialog, {
  deliverableItemOrderFragment,
} from 'components/rewards/deliverableItem/DeliverableItemOrderFormDialog';
import { LastFixtureStoryStep } from 'components/so5/LastFixtureStory/LastFixtureStoryStep';
import { StoryState } from 'components/so5/LastFixtureStory/types';
import useClaimRewards from 'hooks/so5/useClaimRewards';

import { Title } from '../LastFixtureStoryStep/Title';
import { VerificationBanner } from '../VerificationBanner';
import { useResultsStoryAssets } from '../useResultsStoryAssets';
import {
  RewardsRecap_so5Fixture,
  RewardsRecap_so5Reward,
} from './__generated__/index.graphql';
import type {
  UnclaimedDeliverableItemOrders,
  UnclaimedDeliverableItemOrdersVariables,
} from './__generated__/index.graphql';
import { useRewardsVerification } from './useRewardsVerification';

const FullWidthVertical = styled(Vertical).attrs({ gap: 3 })`
  width: 100%;
`;
const FullWidthHorizontal = styled(Horizontal)`
  width: 100%;
  height: var(--quadruple-unit);
  justify-content: space-between;
`;
const DeliverableItemsWrapper = styled(Vertical).attrs({ gap: 0.5 })`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background: var(--c-nd-50);
`;

type CtaProps = {
  box: number;
  card: number;
  onNext?: () => void;
  onClose?: () => Promise<void>;
};
const Cta = ({ box, card, onNext, onClose }: CtaProps) => {
  const { generateSportContextPath } = useSportContext();
  const navigate = useNavigate();

  if (card > 0 && onNext) {
    return (
      <Button size="medium" fullWidth color="secondary" onClick={onNext}>
        <FormattedMessage
          id="RewardsRecap.cta.revealMyCard"
          defaultMessage="Reveal {card, plural, one {card} other {cards}}"
          values={{ card }}
        />
      </Button>
    );
  }
  if (box > 0 && !onNext) {
    return (
      <Button
        size="medium"
        fullWidth
        color="secondary"
        onClick={async () => {
          await onClose?.();
          navigate(generateSportContextPath(ANY_SPORT_MY_CARDS_BOXES));
        }}
      >
        <FormattedMessage
          id="CardRewardsReveal.cta.goToMyBoxes"
          defaultMessage="Go to My Boxes"
        />
      </Button>
    );
  }
  return null;
};

const UNCLAIMED_DELIVERABLE_ITEM_ORDERS = gql`
  query UnclaimedDeliverableItemOrders($sport: Sport) {
    currentUser {
      slug
      unclaimedDeliverableItemOrders(sport: $sport) {
        id
        deliverableItem {
          slug
          id
          name
          pictureUrl
        }
        ...DeliverableItemOrderFormDialog_deliverableItemOrderFragment
      }
    }
  }
  ${deliverableItemOrderFragment}
` as TypedDocumentNode<
  UnclaimedDeliverableItemOrders,
  UnclaimedDeliverableItemOrdersVariables
>;

type Props = {
  so5Fixture?: RewardsRecap_so5Fixture;
  so5Rewards: RewardsRecap_so5Reward[];
  onNext?: () => void;
  onClose: ComponentProps<typeof LastFixtureStoryStep>['onClose'];
  from?: StoryState;
};
export const RewardsRecap = ({
  from: fromProp,
  so5Fixture,
  so5Rewards,
  onNext,
  onClose,
}: Props) => {
  const { sport } = useSportContext();
  const from = useRef(fromProp);
  const { claimableRewardsByType, rewardsByType, verificationNeeded } =
    useRewardsVerification(so5Rewards);

  const [claimedDeliverableItemOrderIds, setClaimedDeliverableItemOrderIds] =
    useState<string[]>([]);
  const [deliverableItemOrderToConfirm, setDeliverableItemOrderToConfirm] =
    useState<
      | NonNullable<
          UnclaimedDeliverableItemOrders['currentUser']
        >['unclaimedDeliverableItemOrders'][number]
      | null
    >(null);
  const [claimed, setClaimed] = useState(false);
  const [claimRewards] = useClaimRewards();

  const { introToRecap, promotionToRecap, recapToEnd, relegationToRecap } =
    useResultsStoryAssets();

  const { data } = useQuery(UNCLAIMED_DELIVERABLE_ITEM_ORDERS, {
    variables: { sport },
    skip: !claimed,
  });

  const deliverableItemOrders =
    data?.currentUser?.unclaimedDeliverableItemOrders || [];

  const { total, card, ...cardExcludedClaimableRewards } =
    claimableRewardsByType;
  const cardExcludedClaimableRewardIds = Object.values(
    cardExcludedClaimableRewards
  ).flatMap(reward => reward.map(({ id }) => id));
  if (!claimed && cardExcludedClaimableRewardIds.length) {
    claimRewards(cardExcludedClaimableRewardIds);
    setClaimed(true);
  }

  let introVideo = introToRecap;
  if (from.current === 'promotion') {
    introVideo = promotionToRecap;
  }
  if (from.current === 'relegation') {
    introVideo = relegationToRecap;
  }

  return (
    <LastFixtureStoryStep
      title={<Title type="recap" so5Fixture={so5Fixture} />}
      introVideo={introVideo}
      outroVideo={recapToEnd}
      onNext={onNext}
      onClose={onClose}
      cta={
        rewardsByType.card.length > 0 || rewardsByType.box.length > 0 ? (
          <Cta
            card={rewardsByType.card.length}
            box={rewardsByType.box.length}
            onNext={onNext}
            onClose={onClose}
          />
        ) : null
      }
      banner={<VerificationBanner verificationNeeded={verificationNeeded} />}
    >
      <FullWidthVertical>
        <FixtureOverview
          so5Fixture={so5Fixture}
          so5Rewards={so5Rewards}
          onlyRewards
          verificationNeeded={verificationNeeded}
        />
        {rewardsByType.deliverableItem.length > 0 && (
          <Vertical>
            <LabelM bold color="var(--c-nd-600)">
              <FormattedMessage
                id="RewardsRecap.deliverableItem.title"
                defaultMessage="Deliverable Items"
              />
            </LabelM>
            <DeliverableItemsWrapper>
              {rewardsByType.deliverableItem
                .flatMap(so5Reward => so5Reward.rewards)
                .filter(isTypeFn('DeliverableItemReward'))
                .map(({ deliverableItem }) => {
                  const correspondingDeliverableItemOrder =
                    deliverableItemOrders.find(
                      deliverableItemOrder =>
                        deliverableItemOrder.deliverableItem.slug ===
                        deliverableItem?.slug
                    );
                  const edit =
                    correspondingDeliverableItemOrder &&
                    claimedDeliverableItemOrderIds.includes(
                      correspondingDeliverableItemOrder.id
                    );

                  return (
                    <FullWidthHorizontal key={deliverableItem?.slug}>
                      <Horizontal>
                        <RewardOverviewChip
                          style={
                            {
                              '--color': rgbColorPerRarity.unique,
                            } as CSSProperties
                          }
                        >
                          {correspondingDeliverableItemOrder?.deliverableItem
                            .pictureUrl && (
                            <ResponsiveImg
                              src={
                                correspondingDeliverableItemOrder
                                  .deliverableItem.pictureUrl
                              }
                              alt=""
                              width={20}
                            />
                          )}
                          <LabelS>x1</LabelS>
                        </RewardOverviewChip>
                        {deliverableItem?.name && (
                          <LabelM>{deliverableItem.name}</LabelM>
                        )}
                      </Horizontal>
                      {correspondingDeliverableItemOrder && (
                        <Button
                          color={edit ? 'tertiary' : 'primary'}
                          size="small"
                          onClick={() =>
                            setDeliverableItemOrderToConfirm(
                              correspondingDeliverableItemOrder
                            )
                          }
                        >
                          {edit ? (
                            <FormattedMessage
                              id="RewardsRecap.deliverableItem.editAddress"
                              defaultMessage="Edit address"
                            />
                          ) : (
                            <FormattedMessage
                              id="RewardsRecap.deliverableItem.addAddress"
                              defaultMessage="Add address"
                            />
                          )}
                        </Button>
                      )}
                    </FullWidthHorizontal>
                  );
                })}
            </DeliverableItemsWrapper>
          </Vertical>
        )}
        {deliverableItemOrderToConfirm && (
          <DeliverableItemOrderFormDialog
            open
            deliverableItemOrder={deliverableItemOrderToConfirm}
            onCancel={() => setDeliverableItemOrderToConfirm(null)}
            onDone={() => {
              setClaimedDeliverableItemOrderIds(array => [
                ...array,
                deliverableItemOrderToConfirm.id,
              ]);
              setDeliverableItemOrderToConfirm(null);
            }}
          />
        )}
      </FullWidthVertical>
    </LastFixtureStoryStep>
  );
};

RewardsRecap.fragments = {
  so5Reward: gql`
    fragment RewardsRecap_so5Reward on So5Reward {
      slug
      id
      rewards {
        id
        ... on DeliverableItemReward {
          id
          deliverableItem {
            slug
            name
          }
        }
      }
      ...FixtureOverview_so5RewardOrConfig
      ...useRewardsVerification_so5Reward
    }
    ${FixtureOverview.fragments.so5RewardOrConfig}
    ${useRewardsVerification.fragments.so5Reward}
  ` as TypedDocumentNode<RewardsRecap_so5Reward>,
  so5Fixture: gql`
    fragment RewardsRecap_so5Fixture on So5Fixture {
      slug
      gameWeek
      ...FixtureOverview_so5Fixture
      ...Title_so5Fixture
    }
    ${FixtureOverview.fragments.so5Fixture}
    ${Title.fragments.so5Fixture}
  ` as TypedDocumentNode<RewardsRecap_so5Fixture>,
};
